import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { PriceSpreadsResponse, CompaniesResponse, ListingProfile } from '@/types/security'

/**
 * Hooks for fetching security and company data.
 */

// Query keys for cache management
export const securityKeys = {
  all: ['securities'] as const,
  pricespreads: (params: { page?: number; size?: number; search?: string; sort?: string }) =>
    [...securityKeys.all, 'pricespreads', params] as const,
  companies: (params: { page?: number; size?: number; search?: string; sort?: string }) =>
    [...securityKeys.all, 'companies', params] as const,
  profile: (securityIdentifier: string) =>
    [...securityKeys.all, 'profile', securityIdentifier] as const,
}

interface PriceSpreadsParams {
  page?: number
  size?: number
  search?: string
  sort?: string
}

/**
 * Hook to fetch price spreads (securities list with prices).
 */
export function usePriceSpreads(params: PriceSpreadsParams = {}) {
  const { page = 0, size = 20, search, sort = 'name,asc' } = params

  return useQuery({
    queryKey: securityKeys.pricespreads({ page, size, search, sort }),
    queryFn: () =>
      api.get<PriceSpreadsResponse>('/api/v2/pricespreads', {
        page,
        size,
        search: search || undefined,
        sort,
      }),
    staleTime: 30000, // Cache for 30 seconds (prices change)
  })
}

interface CompaniesParams {
  page?: number
  size?: number
  search?: string
  sort?: string
}

/**
 * Hook to fetch companies list.
 */
export function useCompanies(params: CompaniesParams = {}) {
  const { page = 0, size = 20, search, sort = 'listing.startDate,desc' } = params

  return useQuery({
    queryKey: securityKeys.companies({ page, size, search, sort }),
    queryFn: () =>
      api.get<CompaniesResponse>('/api/v2/companies', {
        page,
        size,
        search: search || undefined,
        sort,
      }),
    staleTime: 60000, // Cache for 1 minute
  })
}

/**
 * Hook to fetch a listing profile by security identifier.
 */
export function useListingProfile(securityIdentifier: string | null) {
  return useQuery({
    queryKey: securityKeys.profile(securityIdentifier || ''),
    queryFn: () =>
      api.get<ListingProfile>('/api/v2/listingprofiles', {
        securityIdentifier: securityIdentifier!,
      }),
    enabled: !!securityIdentifier,
    staleTime: 30000,
  })
}
